/*!
 * Story Video Card
 *
 * Clickable thumbnail that opens a full-screen YouTube player with
 * a close (X) button in the top-right corner. Mobile uses the reel
 * variant by default; desktop uses the wide variant.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "our.story.video",
  title: "Story Video Card",
  description: "Renders a thumbnail and opens a fullscreen YouTube player using reel or wide video IDs.",
  fields: {
    wideVideoId: {
      title: "Wide YouTube Video ID",
      type: "string",
      required: true,
      description: "YouTube video ID for the desktop/wide version."
    },
    reelVideoId: {
      title: "Reel YouTube Video ID",
      type: "string",
      required: true,
      description: "YouTube video ID for the mobile/reel version."
    },
    title: {
      title: "Title",
      type: "string",
      description: "Optional title override for the card."
    },
    description: {
      title: "Description",
      type: "string",
      description: "Optional one-sentence description."
    },
    thumbnail: {
      title: "Thumbnail",
      type: "uri",
      description: "Required for quality; use assets from app/public/images."
    },
    class: {
      title: "Class",
      type: "string",
      description: "Optional classes applied to the root card."
    }
  },
  styling: {
    styles: [
      {
        selector: ".our-story-video-card",
        styles: [
          "display: flex;",
          "flex-direction: column;",
          "gap: .75rem;",
          "width: 100%;",
          "text-align: left;",
          "font-family: Inter, Arial, sans-serif;"
        ]
      },
      {
        selector: ".our-story-video-thumb-btn",
        styles: [
          "position: relative;",
          "display: block;",
          "width: 100%;",
          "padding: 0;",
          "border: 0;",
          "background: none;"
        ]
      },
      {
        selector: ".our-story-video-thumb-wrap",
        styles: [
          "position: relative;",
          "width: 100%;",
          "max-width: 100%;",
          "aspect-ratio: 16 / 9;",
          "display: block;",
          "overflow: hidden;",
          "border-radius: .85rem;",
          "background: #111;",
          "--bs-aspect-ratio: 56.25%;"
        ]
      },
      {
        selector: ".our-story-video-thumb-wrap[data-our-story-video-variant='wide']",
        styles: [
          "--bs-aspect-ratio: 56.25%;",
          "aspect-ratio: 16 / 9;"
        ]
      },
      {
        selector: ".our-story-video-thumb-wrap[data-our-story-video-variant='reel']",
        styles: [
          "--bs-aspect-ratio: 177.7778%;",
          "aspect-ratio: 9 / 16;"
        ]
      },
      {
        selector: ".our-story-video-thumb-image",
        styles: [
          "position: absolute;",
          "inset: 0;",
          "width: 100%;",
          "height: 100%;",
          "object-fit: cover;",
          "object-position: center center;",
          "background: #111;"
        ]
      },
      {
        selector: ".our-story-video-thumb-btn:hover .our-story-video-thumb-image,.our-story-video-thumb-btn:focus-visible .our-story-video-thumb-image",
        styles: [
          "transform: scale(1.02);"
        ]
      },
      {
        selector: ".our-story-video-play-overlay",
        styles: [
          "position: absolute;",
          "inset: 0;",
          "display: flex;",
          "align-items: center;",
          "justify-content: center;",
          "background: linear-gradient(transparent, rgba(0, 0, 0, 0.38));"
        ]
      },
      {
        selector: ".our-story-video-play-icon",
        styles: [
          "width: 3.5rem;",
          "height: 3.5rem;",
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "border-radius: 999px;",
          "background: rgba(0, 0, 0, 0.55);",
          "color: #fff;",
          "border: 2px solid rgba(255, 255, 255, 0.75);",
          "font-size: 1.6rem;",
          "pointer-events: none;"
        ]
      },
      {
        selector: ".our-story-video-content",
        styles: [
          "display: flex;",
          "flex-direction: column;",
          "gap: .35rem;"
        ]
      },
      {
        selector: ".our-story-video-title",
        styles: [
          "font-weight: 700;",
          "font-size: clamp(1.02rem, 1.5vw, 1.18rem);",
          "line-height: 1.35;",
          "margin: 0;",
          "display: -webkit-box;",
          "-webkit-line-clamp: 2;",
          "-webkit-box-orient: vertical;",
          "overflow: hidden;"
        ]
      },
      {
        selector: ".our-story-video-description",
        styles: [
          "color: var(--bs-secondary-color);",
          "margin: 0;",
          "line-height: 1.5;",
          "font-size: .95rem;",
          "display: -webkit-box;",
          "-webkit-line-clamp: 2;",
          "-webkit-box-orient: vertical;",
          "overflow: hidden;"
        ]
      },
      {
        selector: ".our-story-video-modal",
        styles: [
          "position: fixed;",
          "inset: 0;",
          "background: rgba(0, 0, 0, 0.95);",
          "z-index: 2147483647;",
          "display: flex;",
          "align-items: center;",
          "justify-content: center;",
          "padding: 1rem;"
        ]
      },
      {
        selector: ".our-story-video-overlay-wrap",
        styles: [
          "position: absolute;",
          "inset: 0;",
          "display: flex;",
          "align-items: center;",
          "justify-content: center;"
        ]
      },
      {
        selector: ".our-story-video-frame-wrap",
        styles: [
          "position: relative;",
          "width: min(100vw, 100vh * 16 / 9);",
          "aspect-ratio: 16 / 9;",
          "max-width: 100%;",
          "max-height: 100%;",
          "border-radius: .75rem;",
          "overflow: hidden;",
          "background: #000;",
          "box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.55);"
        ]
      },
      {
        selector: ".our-story-video-loading",
        styles: [
          "position: absolute;",
          "inset: 0;",
          "display: flex;",
          "align-items: center;",
          "justify-content: center;",
          "background: #000;",
          "z-index: 1;",
          "transition: opacity .28s ease;",
          "pointer-events: none;"
        ]
      },
      {
        selector: ".our-story-video-loading.is-loaded",
        styles: [
          "opacity: 0;"
        ]
      },
      {
        selector: ".our-story-video-loading-logo",
        styles: [
          "width: 4rem;",
          "height: 4rem;",
          "opacity: .9;"
        ]
      },
      {
        selector: ".our-story-video-modal[data-our-story-video-display='reel'] .our-story-video-frame-wrap",
        styles: [
          "width: min(100vw, 100vh * 9 / 16);",
          "aspect-ratio: 9 / 16;"
        ]
      },
      {
        selector: ".our-story-video-frame",
        styles: [
          "position: absolute;",
          "inset: 0;",
          "width: 100%;",
          "height: 100%;",
          "border: 0;"
        ]
      },
      {
        selector: ".our-story-video-close",
        styles: [
          "position: absolute;",
          "top: .65rem;",
          "right: .65rem;",
          "z-index: 2;",
          "width: 3rem;",
          "height: 3rem;",
          "padding: 0;",
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "border-radius: 999px;",
          "background: rgba(255, 255, 255, 0.18);",
          "color: #fff;",
          "border: 0;",
          "font-size: 1.45rem;",
          "backdrop-filter: blur(2px);"
        ]
      },
      {
        selector: ".our-story-video-close:hover,.our-story-video-close:focus-visible",
        styles: [
          "background: rgba(255, 255, 255, 0.32);"
        ]
      },
      {
        selector: "@media (min-width: 768px)",
        styles: [
          ".our-story-video-modal[data-our-story-video-display='wide'] .our-story-video-frame-wrap { width: min(100vw, 100vh * 16 / 9); }"
        ]
      },
      {
        selector: "@media (max-width: 767.98px)",
        styles: [
          ".our-story-video-modal[data-our-story-video-display='reel'] .our-story-video-frame-wrap { width: min(100vw, 100vh * 9 / 16); }"
        ]
      }
    ]
  }
});

function safeText(value, fallback) {
  var text = "";
  if (typeof value === "string") {
    text = value.trim();
  } else if (value && value.toString) {
    text = (value.toString() || "").trim();
  }

  if (text.length) {
    return text;
  }

  return typeof fallback === "string" ? fallback : "";
}

function splitWords(value) {
  return safeText(value).split(/\s+/).filter(function(word) {
    return !!word;
  });
}

function isClientRender() {
  return (
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    !window.UATisServer
  );
}

function isMobileViewport() {
  if (!(isClientRender())) {
    return false;
  }

  return window.innerWidth < 768;
}

function sentenceFromText(text) {
  var safe = safeText(text);
  if (!safe) {
    return "";
  }

  var sentenceEnd = safe.length;
  [".", "?", "!"].forEach(function(token) {
    var idx = safe.indexOf(token);
    if (idx !== -1 && idx < sentenceEnd) {
      sentenceEnd = idx;
    }
  });

  if (sentenceEnd > 0 && sentenceEnd < safe.length) {
    if (safe[sentenceEnd] === "\n") {
      return safe.slice(0, sentenceEnd).trim();
    }
    return safe.slice(0, sentenceEnd + 1).trim();
  }

  return safe;
}

function titleFromValue(title) {
  var words = splitWords(title);

  if (words.length > 10) {
    words = words.slice(0, 10);
  }

  if (words.length < 7) {
    return words.join(" ");
  }

  return words.join(" ");
}

function descriptionFromValue(value) {
  var sentence = sentenceFromText(value);
  if (!sentence) {
    return "Watch this short story to see how Whitehall Police serves with training, discipline, and community focus.";
  }

  return sentence;
}

function thumbnailFromState(thumbnail, fallbackVideoId) {
  var custom = safeText(thumbnail);
  if (custom) {
    return custom;
  }

  if (fallbackVideoId) {
    return "https://img.youtube.com/vi/" + encodeURIComponent(fallbackVideoId) + "/hqdefault.jpg";
  }

  return "";
}

function embedUrl(videoId, variant) {
  if (!videoId) {
    return "";
  }

  return (
    "https://www.youtube.com/embed/" +
    encodeURIComponent(videoId) +
    "?autoplay=1&mute=0&controls=1&rel=0&playsinline=0&modestbranding=1&fs=1&enablejsapi=1&iv_load_policy=3&showinfo=0&autohide=1&color=white&vq=hd1080"
  );
}

function ensureUniqueId() {
  return "our-story-video-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function buildRenderableState(options) {
  var wideVideoId = safeText(options.wideVideoId);
  var reelVideoId = safeText(options.reelVideoId);

  if (!(wideVideoId) && reelVideoId) {
    wideVideoId = reelVideoId;
  }

  if (!(reelVideoId) && wideVideoId) {
    reelVideoId = wideVideoId;
  }

  return {
    wideVideoId: wideVideoId,
    reelVideoId: reelVideoId,
    title: titleFromValue(options.title),
    description: descriptionFromValue(options.description),
    thumbnail: thumbnailFromState(options.thumbnail, wideVideoId || reelVideoId)
  };
}

function resolveActiveVariantByViewport(model) {
  var wantsReel = isMobileViewport();
  if (wantsReel && model.reelVideoId) {
    return "reel";
  }

  return model.wideVideoId ? "wide" : "reel";
}

function resolveVideoId(model, variant) {
  if (variant === "reel") {
    return model.reelVideoId || model.wideVideoId;
  }

  return model.wideVideoId || model.reelVideoId;
}

exports.render = async function(options) {
  var model = buildRenderableState({
    wideVideoId: options.wideVideoId,
    reelVideoId: options.reelVideoId,
    title: options.title,
    description: options.description,
    thumbnail: options.thumbnail
  });

  var modalId = ensureUniqueId();
  var cardClass = safeText(options.class, "");

  return {
    n: "div",
    c: "our-story-video-card" + (cardClass ? " " + cardClass : ""),
    i: [
      {
        n: "button",
        type: "button",
        c: "our-story-video-thumb-btn",
        "data-our-story-video-open": true,
        "data-our-story-video-modal": modalId,
        "data-our-story-video-wide-id": model.wideVideoId,
        "data-our-story-video-reel-id": model.reelVideoId,
        "data-our-story-video-title": model.title,
        "data-our-story-video-active-variant": "wide",
        "aria-label": "Play video: " + model.title,
        i: {
        n: "span",
        c: "ratio our-story-video-thumb-wrap",
        "data-our-story-video-variant": "wide",
        i: [
            {
              n: "img",
              c: "our-story-video-thumb-image",
              src: model.thumbnail,
              alt: model.title,
              loading: "lazy"
            },
            {
              n: "span",
              c: "our-story-video-play-overlay",
              i: {
                n: "span",
                c: "our-story-video-play-icon",
                i: {
                  n: "i",
                  c: "bi bi-play-fill"
                }
              }
            }
          ]
        }
      },
      {
        n: "div",
        c: "our-story-video-content",
        i: [
          {
            n: "p",
            c: "our-story-video-title",
            i: model.title
          },
          {
            n: "p",
            c: "our-story-video-description",
            i: model.description
          }
        ]
      },
      {
        n: "div",
        c: "our-story-video-modal d-none",
        id: modalId,
        "data-our-story-video-overlay": true,
        "data-our-story-video-display": "wide",
        i: {
          n: "div",
          c: "our-story-video-overlay-wrap",
          i: [
            {
              n: "button",
              c: "our-story-video-close",
              type: "button",
              "data-our-story-video-close": modalId,
              "aria-label": "Close video",
              i: {
                n: "i",
                c: "bi bi-x-lg"
              }
            },
            {
              n: "div",
              c: "our-story-video-frame-wrap",
              i: [
                {
                  n: "div",
                  c: "our-story-video-loading",
                  "data-our-story-video-loading": modalId,
                  i: {
                    n: "img",
                    c: "our-story-video-loading-logo",
                    src: "/logo.svg",
                    alt: "Whitehall logo"
                  }
                },
                {
                  n: "iframe",
                  c: "our-story-video-frame",
                  "data-our-story-video-player": modalId,
                  title: model.title,
                  src: "",
                  frameborder: "0",
                  allow: "autoplay; fullscreen; encrypted-media; picture-in-picture",
                  allowfullscreen: "allowfullscreen",
                  loading: "eager"
                }
              ]
            }
          ]
        }
      }
    ]
  };
};

function setCardVariant(cardButton, isMobile) {
  if (!cardButton) {
    return;
  }

  var wideVideoId = (cardButton.getAttribute("data-our-story-video-wide-id") || "").trim();
  var reelVideoId = (cardButton.getAttribute("data-our-story-video-reel-id") || "").trim();

  var variant = isMobile ? "reel" : "wide";

  if (isMobile && !reelVideoId && wideVideoId) {
    variant = "wide";
  }

  if (!isMobile && !wideVideoId && reelVideoId) {
    variant = "reel";
  }

  // The thumbnail box stays 16:9 on every screen (the thumbnails are wide art, so a 9:16 box showed black bars);
  // only the player changes: phones still open the reel full-screen, desktop opens the wide cut.
  var thumbWrap = cardButton.querySelector(".our-story-video-thumb-wrap");
  if (thumbWrap) {
    thumbWrap.setAttribute("data-our-story-video-variant", "wide");
    thumbWrap.style.setProperty("--bs-aspect-ratio", "56.25%");
  }
  cardButton.setAttribute("data-our-story-video-active-variant", variant);
}

function setOverlayMode(overlay, isMobile) {
  if (!overlay) {
    return;
  }

  var variant = isMobile ? "reel" : "wide";
  overlay.setAttribute("data-our-story-video-display", variant);
}

function openModal(openButton) {
  var modalId = openButton.getAttribute("data-our-story-video-modal");
  var modal = document.getElementById(modalId);
  if (!modal) {
    return;
  }

  var model = {
    wideVideoId: openButton.getAttribute("data-our-story-video-wide-id") || "",
    reelVideoId: openButton.getAttribute("data-our-story-video-reel-id") || ""
  };
  var variant = resolveActiveVariantByViewport(model);
  var videoId = resolveVideoId(model, variant);
  var iframe = modal.querySelector("[data-our-story-video-player]");
  var loading = modal.querySelector("[data-our-story-video-loading]");

  if (!(iframe) || !(videoId)) {
    return;
  }

  if (loading) {
    loading.classList.remove("is-loaded");
  }
  iframe.onload = function() {
    if (loading) {
      loading.classList.add("is-loaded");
    }
  };
  setOverlayMode(modal, variant === "reel");
  iframe.src = embedUrl(videoId, variant);
  modal.classList.remove("d-none");
  document.body.classList.add("overflow-hidden");
}

function closeModalById(modalId) {
  var modal = document.getElementById(modalId);
  if (!modal) {
    return;
  }

  var iframe = modal.querySelector("[data-our-story-video-player]");
  var loading = modal.querySelector("[data-our-story-video-loading]");
  if (iframe) {
    iframe.onload = null;
    iframe.src = "";
  }
  if (loading) {
    loading.classList.remove("is-loaded");
  }

  modal.classList.add("d-none");
  document.body.classList.remove("overflow-hidden");
}

function handleVideoClick(e) {
  var openButton = e.target.closest("[data-our-story-video-open]");
  if (openButton) {
    e.preventDefault();
    openModal(openButton);
    return;
  }

  var closeButton = e.target.closest("[data-our-story-video-close]");
  if (closeButton) {
    e.preventDefault();
    closeModalById(closeButton.getAttribute("data-our-story-video-close"));
    return;
  }

  var overlay = e.target.closest("[data-our-story-video-overlay]");
  if (overlay && e.target === overlay) {
    closeModalById(overlay.id);
  }
}

function handleVideoResize() {
  var isMobile = isMobileViewport();
  var cards = document.querySelectorAll("[data-our-story-video-open]");
  for (var i = 0; i < cards.length; i++) {
    setCardVariant(cards[i], isMobile);
  }

  var overlays = document.querySelectorAll(".our-story-video-modal:not(.d-none)");
  for (var j = 0; j < overlays.length; j++) {
    setOverlayMode(overlays[j], isMobile);
  }
}

function handleEscape(e) {
  if (e.key === "Escape") {
    var overlay = document.querySelector(".our-story-video-modal:not(.d-none)");
    if (overlay) {
      closeModalById(overlay.id);
    }
  }
}

var initializedClient = false;

function init() {
  if (!isClientRender()) {
    return;
  }

  if (initializedClient === true) {
    handleVideoResize();
    return;
  }

  initializedClient = true;

  handleVideoResize();
  document.addEventListener("click", handleVideoClick);
  document.addEventListener("keydown", handleEscape);
  window.addEventListener("resize", handleVideoResize);
}

var events = require("../../uam/events.js");
events.on("interface.afterrender", function() {
  init();
  if (isClientRender()) {
    handleVideoResize();
  }
});
